import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  Loading,
  Pill,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { money } from '../lib/format';
import type { ApplicationDetail, CheckResult } from '../lib/types';

const STEPS = [
  { n: 1, name: 'Business profile', hint: 'Legal entity, DBA, location' },
  { n: 2, name: 'Ownership & control', hint: 'Principals at 25% or more' },
  { n: 3, name: 'Banking & processing', hint: 'Settlement, volumes, history' },
  { n: 4, name: 'Pricing & equipment', hint: 'Schedule, fees, terminals' },
  { n: 5, name: 'Review & submit', hint: 'Disclosures and signature' },
];

const HEADINGS: Array<[string, string]> = [
  [
    'Tell us about the business',
    'The legal entity as it appears on the tax return, plus the name customers will see on their statement.',
  ],
  [
    'Who owns and controls it',
    'Every beneficial owner at 25% or more, and one control person who can bind the business.',
  ],
  [
    'Banking and processing profile',
    'Where funds settle, and what the card activity actually looks like.',
  ],
  ['Pricing and equipment', 'The schedule the merchant signs, and what ships on approval.'],
  ['Review and submit', 'Last look before it goes to underwriting.'],
];

const MCC_OPTIONS = [
  { code: '7216', label: '7216 — Dry cleaners' },
  { code: '5812', label: '5812 — Eating places, restaurants' },
  { code: '5499', label: '5499 — Misc. food stores' },
  { code: '8021', label: '8021 — Dentists, orthodontists' },
  { code: '7538', label: '7538 — Automotive service shops' },
  { code: '5261', label: '5261 — Lawn and garden supply' },
  { code: '8099', label: '8099 — Health practitioners' },
];

/** Creates the draft on mount, then hands off to the editor. */
export function NewApplication() {
  const navigate = useNavigate();
  const [error, setError] = useState<ApiError | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in development; without this guard
    // every visit would leave an orphan draft behind.
    if (started.current) return;
    started.current = true;

    api
      .post<ApplicationDetail>('/applications', {})
      .then((app) => navigate(`/applications/${app.id}`, { replace: true }))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err : new ApiError(0, 'Could not start a draft', null)),
      );
  }, [navigate]);

  if (error) {
    return (
      <>
        <PageHeader breadcrumb={['Applications']} title="New application" />
        <PageBody>
          <ErrorState error={error} />
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader breadcrumb={['Applications']} title="New application" />
      <PageBody>
        <Loading label="Starting a draft…" />
      </PageBody>
    </>
  );
}

/** The wizard itself, used by the application detail route for a draft. */
export function ApplicationWizard({
  app,
  onSaved,
}: {
  app: ApplicationDetail;
  onSaved: (next: ApplicationDetail) => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(app);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);

  const [heading, sub] = HEADINGS[step - 1];

  function patch(partial: Partial<ApplicationDetail>) {
    setDraft((d) => ({ ...d, ...partial }));
  }

  async function save(): Promise<ApplicationDetail | null> {
    setSaving(true);
    setSubmitError(null);
    try {
      const body = {
        business: draft.business,
        principals: draft.principals,
        banking: draft.banking,
        processing: draft.processing,
        history: draft.history,
        disclosures: draft.disclosures,
      };
      const next = await api.patch<ApplicationDetail>(`/applications/${draft.id}`, body);
      setDraft(next);
      setSavedAt(new Date());
      onSaved(next);
      return next;
    } catch (err) {
      if (err instanceof ApiError) setSubmitError(err);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    const saved = await save();
    if (!saved) return;
    setSaving(true);
    try {
      const next = await api.post<ApplicationDetail>(`/applications/${draft.id}/submit`, {
        sendForSignatureTo: draft.business.email || undefined,
      });
      onSaved(next);
      navigate(`/applications/${next.id}`);
    } catch (err) {
      if (err instanceof ApiError) setSubmitError(err);
    } finally {
      setSaving(false);
    }
  }

  const ownership = draft.principals.reduce((s, p) => s + (p.ownershipPercent || 0), 0);
  const remaining = Math.max(0, 100 - ownership);

  return (
    <>
      <PageHeader
        breadcrumb={['Applications', draft.reference, 'Draft']}
        title="Merchant processing application"
        actions={
          <>
            <span className="card__meta">
              {saving
                ? 'Saving…'
                : savedAt
                  ? `Saved ${savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Not saved yet'}
            </span>
            <Button onClick={() => void save().then(() => navigate('/applications'))}>
              Save &amp; exit
            </Button>
          </>
        }
      />

      <ScopeBar>
        Writing for <strong>{user?.nodeName}</strong> · pricing below your floor routes to{' '}
        <strong>{user?.parentNodeName ?? 'your parent office'}</strong> first
      </ScopeBar>

      <PageBody>
        <div className="wizard">
          <Card>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="summarycard__label">STEP {step} OF 5</span>
              <span className="card__meta">{step * 20}%</span>
            </div>
            <div className="progress">
              <div className="progress__fill" style={{ width: `${step * 20}%` }} />
            </div>
            <div className="steps">
              {STEPS.map((s) => (
                <button
                  key={s.n}
                  type="button"
                  className={`step ${s.n === step ? 'is-current' : ''} ${s.n < step ? 'is-done' : ''}`}
                  onClick={() => setStep(s.n)}
                >
                  <span className="step__badge">{s.n < step ? '✓' : s.n}</span>
                  <span>
                    <span className="step__name">{s.name}</span>
                    <span className="step__hint">{s.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title={heading} />
            <p className="login__lede" style={{ marginTop: -8 }}>
              {sub}
            </p>

            {submitError && (
              <div className="notice notice--danger" role="alert" style={{ marginBottom: 14 }}>
                {Icon.alert({ size: 15 })}
                <span>
                  {submitError.message}
                  {submitError.failures.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {submitError.failures.map((f) => (
                        <li key={f.id}>
                          {f.label}
                          {f.detail ? ` — ${f.detail}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </span>
              </div>
            )}

            {step === 1 && (
              <div className="formgrid">
                <div className="formgrid formgrid--2">
                  <Text
                    label="Legal business name"
                    value={draft.business.legalName}
                    onChange={(v) =>
                      patch({ business: { ...draft.business, legalName: v } })
                    }
                    placeholder="Bellamy Holdings LLC"
                  />
                  <Text
                    label="DBA / trade name"
                    value={draft.business.dba}
                    onChange={(v) => patch({ business: { ...draft.business, dba: v } })}
                    placeholder="Bellamy Cleaners #2"
                  />
                </div>
                <div className="formgrid formgrid--3">
                  <Text
                    label="Federal tax ID (EIN)"
                    value={draft.business.ein ?? ''}
                    onChange={(v) => patch({ business: { ...draft.business, ein: v } })}
                    placeholder="00-0000000"
                  />
                  <Select
                    label="Entity type"
                    value={draft.business.entityType}
                    onChange={(v) =>
                      patch({ business: { ...draft.business, entityType: v } })
                    }
                    options={[
                      ['llc', 'LLC'],
                      ['sole_proprietor', 'Sole proprietor'],
                      ['c_corp', 'C corporation'],
                      ['s_corp', 'S corporation'],
                      ['partnership', 'Partnership'],
                      ['non_profit', 'Non-profit'],
                    ]}
                  />
                  <Text
                    label="Years in business"
                    value={String(draft.business.yearsInBusiness ?? '')}
                    onChange={(v) =>
                      patch({
                        business: {
                          ...draft.business,
                          yearsInBusiness: v ? Number(v) : undefined,
                        },
                      })
                    }
                    placeholder="6"
                  />
                </div>
                <div className="formgrid formgrid--2">
                  <Select
                    label="Merchant category (MCC)"
                    value={draft.business.mcc}
                    onChange={(v) => {
                      const opt = MCC_OPTIONS.find((o) => o.code === v);
                      patch({
                        business: {
                          ...draft.business,
                          mcc: v,
                          mccDescription: opt?.label.split('— ')[1] ?? '',
                        },
                      });
                    }}
                    options={[
                      ['', 'Choose a category'],
                      ...MCC_OPTIONS.map((o) => [o.code, o.label] as [string, string]),
                    ]}
                  />
                  <Text
                    label="Goods or services sold"
                    value={draft.business.goodsOrServices}
                    onChange={(v) =>
                      patch({ business: { ...draft.business, goodsOrServices: v } })
                    }
                    placeholder="Garment cleaning and alterations"
                  />
                </div>

                <fieldset className="fieldset">
                  <legend>Location</legend>
                  <div className="formgrid formgrid--4">
                    <Text
                      label="Street address"
                      value={draft.business.address.street}
                      onChange={(v) =>
                        patch({
                          business: {
                            ...draft.business,
                            address: { ...draft.business.address, street: v },
                          },
                        })
                      }
                    />
                    <Text
                      label="City"
                      value={draft.business.address.city}
                      onChange={(v) =>
                        patch({
                          business: {
                            ...draft.business,
                            address: { ...draft.business.address, city: v },
                          },
                        })
                      }
                    />
                    <Text
                      label="State"
                      value={draft.business.address.state}
                      onChange={(v) =>
                        patch({
                          business: {
                            ...draft.business,
                            address: { ...draft.business.address, state: v },
                          },
                        })
                      }
                    />
                    <Text
                      label="ZIP"
                      value={draft.business.address.zip}
                      onChange={(v) =>
                        patch({
                          business: {
                            ...draft.business,
                            address: { ...draft.business.address, zip: v },
                          },
                        })
                      }
                    />
                  </div>
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={draft.business.mailingSameAsAddress}
                      onChange={(e) =>
                        patch({
                          business: {
                            ...draft.business,
                            mailingSameAsAddress: e.target.checked,
                          },
                        })
                      }
                    />
                    Mailing address is the same
                  </label>
                </fieldset>

                <div className="formgrid formgrid--3">
                  <Text
                    label="Business phone"
                    value={draft.business.phone}
                    onChange={(v) => patch({ business: { ...draft.business, phone: v } })}
                    type="tel"
                  />
                  <Text
                    label="Contact email"
                    value={draft.business.email}
                    onChange={(v) => patch({ business: { ...draft.business, email: v } })}
                    type="email"
                  />
                  <Text
                    label="Website"
                    value={draft.business.website ?? ''}
                    onChange={(v) => patch({ business: { ...draft.business, website: v } })}
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="formgrid">
                <div className="notice notice--info">
                  {Icon.alert({ size: 15 })}
                  <span>
                    Card network rules require every beneficial owner at 25% or more, plus
                    one control person. Ownership across all principals must total 100%.
                  </span>
                </div>

                {draft.principals.map((p, i) => (
                  <fieldset className="fieldset" key={i}>
                    <legend>Principal {i + 1}</legend>
                    <div className="row">
                      {p.isControlPerson && <Pill tone="info">Control person</Pill>}
                      <span className="spacer" />
                      <span className="card__meta">{p.ownershipPercent}% ownership</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          patch({
                            principals: draft.principals.filter((_, j) => j !== i),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="formgrid formgrid--4">
                      <Text
                        label="First name"
                        value={p.firstName}
                        onChange={(v) => updatePrincipal(i, { firstName: v })}
                      />
                      <Text
                        label="Last name"
                        value={p.lastName}
                        onChange={(v) => updatePrincipal(i, { lastName: v })}
                      />
                      <Text
                        label="Title"
                        value={p.title}
                        onChange={(v) => updatePrincipal(i, { title: v })}
                        placeholder="Managing member"
                      />
                      <Text
                        label="Ownership %"
                        value={String(p.ownershipPercent || '')}
                        onChange={(v) =>
                          updatePrincipal(i, { ownershipPercent: Number(v) || 0 })
                        }
                      />
                    </div>
                    <div className="formgrid formgrid--4">
                      <Text
                        label="SSN — last 4"
                        value={p.ssnLast4 ?? ''}
                        onChange={(v) => updatePrincipal(i, { ssnLast4: v.slice(0, 4) })}
                        placeholder="••••"
                      />
                      <Text
                        label="Date of birth"
                        value={p.dateOfBirth ?? ''}
                        onChange={(v) => updatePrincipal(i, { dateOfBirth: v })}
                        placeholder="MM / DD / YYYY"
                      />
                      <Text
                        label="Mobile phone"
                        value={p.mobile ?? ''}
                        onChange={(v) => updatePrincipal(i, { mobile: v })}
                        type="tel"
                      />
                      <Text
                        label="Email"
                        value={p.email ?? ''}
                        onChange={(v) => updatePrincipal(i, { email: v })}
                        type="email"
                      />
                    </div>
                    <Text
                      label="Home address"
                      value={p.homeAddress ?? ''}
                      onChange={(v) => updatePrincipal(i, { homeAddress: v })}
                    />
                    <label className="checkrow">
                      <input
                        type="checkbox"
                        checked={p.isControlPerson}
                        onChange={(e) => {
                          // Exactly one control person, so selecting one clears the rest.
                          const next = draft.principals.map((q, j) => ({
                            ...q,
                            isControlPerson: j === i ? e.target.checked : false,
                          }));
                          patch({ principals: next });
                        }}
                      />
                      This person is the control person who can bind the business
                    </label>
                    <label className="checkrow">
                      <input
                        type="checkbox"
                        checked={p.idUploaded}
                        onChange={(e) => updatePrincipal(i, { idUploaded: e.target.checked })}
                      />
                      Government photo ID uploaded — front and back
                    </label>
                  </fieldset>
                ))}

                <Button
                  variant="secondary"
                  icon={Icon.plus({ size: 14 })}
                  onClick={() =>
                    patch({
                      principals: [
                        ...draft.principals,
                        {
                          firstName: '',
                          lastName: '',
                          title: '',
                          ownershipPercent: remaining,
                          isControlPerson: draft.principals.length === 0,
                          idUploaded: false,
                        },
                      ],
                    })
                  }
                >
                  Add another principal
                  {remaining > 0 ? ` — ${remaining}% remaining` : ''}
                </Button>

                {ownership !== 100 && draft.principals.length > 0 && (
                  <div className="notice notice--warn">
                    {Icon.alert({ size: 15 })}
                    <span>Ownership totals {ownership}%. It must reach exactly 100%.</span>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="formgrid">
                <fieldset className="fieldset">
                  <legend>Settlement account</legend>
                  <div className="formgrid formgrid--4">
                    <Text
                      label="Bank name"
                      value={draft.banking.bankName ?? ''}
                      onChange={(v) => patch({ banking: { ...draft.banking, bankName: v } })}
                    />
                    <Text
                      label="Routing number"
                      value={draft.banking.routingNumber ?? ''}
                      onChange={(v) =>
                        patch({ banking: { ...draft.banking, routingNumber: v } })
                      }
                      placeholder="000000000"
                    />
                    <Text
                      label="Account — last 4"
                      value={draft.banking.accountLast4 ?? ''}
                      onChange={(v) =>
                        patch({ banking: { ...draft.banking, accountLast4: v.slice(0, 4) } })
                      }
                      placeholder="0000"
                    />
                    <Select
                      label="Type"
                      value={draft.banking.accountType}
                      onChange={(v) =>
                        patch({
                          banking: {
                            ...draft.banking,
                            accountType: v as 'checking' | 'savings',
                          },
                        })
                      }
                      options={[
                        ['checking', 'Checking'],
                        ['savings', 'Savings'],
                      ]}
                    />
                  </div>
                </fieldset>

                <fieldset className="fieldset">
                  <legend>Processing profile</legend>
                  <div className="formgrid formgrid--4">
                    <Money
                      label="Average ticket"
                      cents={draft.processing.averageTicketCents}
                      onChange={(c) =>
                        patch({ processing: { ...draft.processing, averageTicketCents: c } })
                      }
                    />
                    <Money
                      label="Highest ticket"
                      cents={draft.processing.highestTicketCents}
                      onChange={(c) =>
                        patch({ processing: { ...draft.processing, highestTicketCents: c } })
                      }
                    />
                    <Money
                      label="Monthly volume"
                      cents={draft.processing.monthlyVolumeCents}
                      onChange={(c) =>
                        patch({
                          processing: {
                            ...draft.processing,
                            monthlyVolumeCents: c,
                            annualVolumeCents: c * 12,
                          },
                        })
                      }
                    />
                    <Money
                      label="Annual volume"
                      cents={draft.processing.annualVolumeCents}
                      onChange={(c) =>
                        patch({ processing: { ...draft.processing, annualVolumeCents: c } })
                      }
                    />
                  </div>
                  <div className="formgrid formgrid--3">
                    <Text
                      label="Card present %"
                      value={String(draft.processing.cardPresentPercent)}
                      onChange={(v) =>
                        patch({
                          processing: {
                            ...draft.processing,
                            cardPresentPercent: Number(v) || 0,
                          },
                        })
                      }
                    />
                    <Text
                      label="Keyed %"
                      value={String(draft.processing.keyedPercent)}
                      onChange={(v) =>
                        patch({
                          processing: { ...draft.processing, keyedPercent: Number(v) || 0 },
                        })
                      }
                    />
                    <Text
                      label="E-commerce %"
                      value={String(draft.processing.ecommercePercent)}
                      onChange={(v) =>
                        patch({
                          processing: {
                            ...draft.processing,
                            ecommercePercent: Number(v) || 0,
                          },
                        })
                      }
                    />
                  </div>
                </fieldset>

                <fieldset className="fieldset">
                  <legend>Processing history</legend>
                  <div className="formgrid formgrid--3">
                    <Text
                      label="Current / prior processor"
                      value={draft.history.priorProcessor ?? ''}
                      onChange={(v) =>
                        patch({ history: { ...draft.history, priorProcessor: v } })
                      }
                      placeholder="None — first-time merchant"
                    />
                    <Select
                      label="Ever terminated?"
                      value={draft.history.everTerminated ? 'yes' : 'no'}
                      onChange={(v) =>
                        patch({
                          history: { ...draft.history, everTerminated: v === 'yes' },
                        })
                      }
                      options={[
                        ['no', 'No'],
                        ['yes', 'Yes — explain below'],
                      ]}
                    />
                    <Text
                      label="Chargebacks last 6 mo."
                      value={String(draft.history.chargebacksLast6Months)}
                      onChange={(v) =>
                        patch({
                          history: {
                            ...draft.history,
                            chargebacksLast6Months: Number(v) || 0,
                          },
                        })
                      }
                    />
                  </div>
                  {draft.history.everTerminated && (
                    <div className="field">
                      <label className="field__label">Explanation</label>
                      <textarea
                        className="textarea"
                        value={draft.history.terminationExplanation ?? ''}
                        onChange={(e) =>
                          patch({
                            history: {
                              ...draft.history,
                              terminationExplanation: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </fieldset>
              </div>
            )}

            {step === 4 && <PricingStep app={draft} onSaved={onSaved} setDraft={setDraft} />}

            {step === 5 && (
              <div className="formgrid">
                <div className="summarygrid">
                  <SummaryCard
                    label="BUSINESS"
                    value={draft.business.dba || '—'}
                    sub={`${draft.business.legalName || '—'} · ${draft.business.entityType.toUpperCase()} · MCC ${draft.business.mcc || '—'}`}
                    onEdit={() => setStep(1)}
                  />
                  <SummaryCard
                    label="PRINCIPALS"
                    value={`${draft.principals.length} principal${draft.principals.length === 1 ? '' : 's'} · ${ownership}% accounted for`}
                    sub={
                      draft.principals.some((p) => p.isControlPerson)
                        ? 'Control person identified'
                        : 'No control person yet'
                    }
                    onEdit={() => setStep(2)}
                  />
                  <SummaryCard
                    label="PROCESSING"
                    value={`${money(draft.processing.monthlyVolumeCents)} / mo · ${money(draft.processing.averageTicketCents)} average`}
                    sub={`${draft.processing.cardPresentPercent}% card present · ${draft.history.everTerminated ? 'prior termination' : 'no prior terminations'}`}
                    onEdit={() => setStep(3)}
                  />
                  <SummaryCard
                    label="PRICING"
                    value={`IC+ ${draft.pricing?.basisPoints ?? 0} bps`}
                    sub={`${draft.equipment.length} equipment line${draft.equipment.length === 1 ? '' : 's'}`}
                    onEdit={() => setStep(4)}
                  />
                </div>

                <fieldset className="fieldset">
                  <legend>Disclosures and signature</legend>
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={draft.disclosures.programGuideDelivered}
                      onChange={(e) =>
                        patch({
                          disclosures: {
                            ...draft.disclosures,
                            programGuideDelivered: e.target.checked,
                          },
                        })
                      }
                    />
                    The merchant has received the Program Guide and the Schedule of Fees.
                  </label>
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={draft.disclosures.identityVerified}
                      onChange={(e) =>
                        patch({
                          disclosures: {
                            ...draft.disclosures,
                            identityVerified: e.target.checked,
                          },
                        })
                      }
                    />
                    I have personally verified the identity of each signer against their
                    photo ID.
                  </label>
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={draft.disclosures.informationAccurate}
                      onChange={(e) =>
                        patch({
                          disclosures: {
                            ...draft.disclosures,
                            informationAccurate: e.target.checked,
                          },
                        })
                      }
                    />
                    The information above is accurate to the best of my knowledge.
                  </label>
                  <Text
                    label="Send for e-signature to"
                    value={draft.business.email}
                    onChange={(v) => patch({ business: { ...draft.business, email: v } })}
                    type="email"
                  />
                </fieldset>
              </div>
            )}

            <div className="wizard__footer">
              <Button disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
                Back
              </Button>
              <span className="spacer" />
              <Button onClick={() => void save()} disabled={saving}>
                Save draft
              </Button>
              <Button
                variant="primary"
                disabled={saving}
                onClick={() => {
                  if (step === 5) void submit();
                  else void save().then(() => setStep((s) => Math.min(5, s + 1)));
                }}
              >
                {step === 5 ? 'Submit to underwriting' : 'Continue'}
              </Button>
            </div>
          </Card>

          <div className="wizard__aside stack">
            <Card>
              <CardHeader title="Automatic checks" />
              <ChecksList checks={draft.checks} />
            </Card>

            <Card>
              <CardHeader title="What happens next" />
              <ol className="steplist">
                <li>Merchant e-signs — usually same day.</li>
                <li>Underwriting reviews within 1 business day.</li>
                <li>Conditions come back to you, not the merchant.</li>
                <li>On approval the MID is issued and hardware ships.</li>
              </ol>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );

  function updatePrincipal(i: number, partial: Partial<ApplicationDetail['principals'][number]>) {
    setDraft((d) => ({
      ...d,
      principals: d.principals.map((p, j) => (j === i ? { ...p, ...partial } : p)),
    }));
  }
}

/** Step 4 saves through its own endpoint so the floor check runs server-side. */
function PricingStep({
  app,
  setDraft,
  onSaved,
}: {
  app: ApplicationDetail;
  setDraft: (a: ApplicationDetail) => void;
  onSaved: (a: ApplicationDetail) => void;
}) {
  const { user } = useAuth();
  const pricing = (app as unknown as { pricing: {
    model: string;
    basisPoints: number;
    perItemCents: number;
    monthlyServiceFeeCents: number;
    pciFeeCents: number;
    belowFloor: boolean;
  } }).pricing;

  const [local, setLocal] = useState(pricing);
  const [error, setError] = useState<string | null>(null);

  async function persist(next: typeof local) {
    setLocal(next);
    try {
      const saved = await api.patch<ApplicationDetail>(`/applications/${app.id}`, {
        pricing: {
          model: next.model,
          basisPoints: next.basisPoints,
          perItemCents: next.perItemCents,
          monthlyServiceFeeCents: next.monthlyServiceFeeCents,
          pciFeeCents: next.pciFeeCents,
        },
      });
      setDraft(saved);
      onSaved(saved);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  return (
    <div className="formgrid">
      <fieldset className="fieldset">
        <legend>Pricing schedule</legend>
        <div className="formgrid formgrid--3">
          {(
            [
              ['interchange_plus', 'Interchange plus', 'Cost + markup'],
              ['flat', 'Flat rate', 'One blended rate on all card types'],
              ['tiered', 'Tiered', 'Qualified / mid / non-qualified'],
            ] as Array<[string, string, string]>
          ).map(([value, name, hint]) => (
            <label className="radiocard" key={value}>
              <input
                type="radio"
                name="pricing-model"
                checked={local.model === value}
                onChange={() => void persist({ ...local, model: value })}
              />
              <span>
                <span className="radiocard__name">{name}</span>
                <span className="radiocard__hint">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="formgrid formgrid--4">
        <Text
          label="Discount markup (bps)"
          value={String(local.basisPoints)}
          onChange={(v) => void persist({ ...local, basisPoints: Number(v) || 0 })}
        />
        <Money
          label="Per-item fee"
          cents={local.perItemCents}
          onChange={(c) => void persist({ ...local, perItemCents: c })}
        />
        <Money
          label="Monthly service fee"
          cents={local.monthlyServiceFeeCents}
          onChange={(c) => void persist({ ...local, monthlyServiceFeeCents: c })}
        />
        <Money
          label="PCI program fee"
          cents={local.pciFeeCents}
          onChange={(c) => void persist({ ...local, pciFeeCents: c })}
        />
      </div>

      {error && (
        <div className="notice notice--danger">
          {Icon.alert({ size: 15 })}
          <span>{error}</span>
        </div>
      )}

      <div className={`notice notice--${local.belowFloor ? 'warn' : 'info'}`}>
        {local.belowFloor ? Icon.alert({ size: 15 }) : Icon.check({ size: 15 })}
        <span>
          {local.belowFloor ? (
            <>
              {local.basisPoints} bps sits below your inherited floor, so this file routes
              to <strong>{user?.parentNodeName ?? 'your parent office'}</strong> before
              underwriting sees it.
            </>
          ) : (
            <>
              {local.basisPoints} bps sits at or above your floor, so no exception is
              needed.
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function ChecksList({ checks }: { checks: CheckResult[] }) {
  if (!checks || checks.length === 0) {
    return <p className="card__meta">Fill in the file to see the checks run.</p>;
  }
  return (
    <div className="checks">
      {checks.map((c) => (
        <div className="check" key={c.id}>
          <span className={`check__icon check__icon--${c.status}`}>
            {c.status === 'pass' ? Icon.check({ size: 11 }) : Icon.alert({ size: 11 })}
          </span>
          <div>
            <div className="check__label">{c.label}</div>
            {c.detail && <div className="check__detail">{c.detail}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  onEdit,
}: {
  label: string;
  value: string;
  sub: string;
  onEdit: () => void;
}) {
  return (
    <div className="summarycard">
      <div className="summarycard__head">
        <span className="summarycard__label">{label}</span>
        <button type="button" className="linkbtn" onClick={onEdit}>
          Edit
        </button>
      </div>
      <div className="summarycard__value">{value}</div>
      <div className="summarycard__sub">{sub}</div>
    </div>
  );
}

// --- small field wrappers --------------------------------------------------

function Text({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <input
        className="input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <select
        className="select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Edits dollars, stores cents — nothing in the UI handles a raw cent value. */
function Money({
  label,
  cents,
  onChange,
}: {
  label: string;
  cents: number;
  onChange: (cents: number) => void;
}) {
  const [text, setText] = useState(cents ? (cents / 100).toString() : '');

  useEffect(() => {
    setText(cents ? (cents / 100).toString() : '');
  }, [cents]);

  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <input
        className="input"
        inputMode="decimal"
        value={text}
        placeholder="0.00"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const n = Number(text.replace(/[^0-9.]/g, ''));
          onChange(Number.isFinite(n) ? Math.round(n * 100) : 0);
        }}
      />
    </div>
  );
}
